"use client";

import { useTranslations } from "@/app/providers";
import { SectionHeader } from "@/components/common/section-header";
import { FocusCardDesktop } from "@/components/sections/focus-card-desktop";
import { FocusCardMobile } from "@/components/sections/focus-card-mobile";
import { FrontendIcon } from "@/components/icons/frontend-icon";
import { MobileIcon } from "@/components/icons/mobile-icon";
import { BackendIcon } from "@/components/icons/backend-icon";
import { DesignIcon } from "@/components/icons/design-icon";
import { AiIcon } from "@/components/icons/ai-icon";
import { TrainingIcon } from "@/components/icons/training-icon";
import { useRef, useState } from "react";
import { useScroll, useTransform, motion } from "motion/react";
import { Cursor } from "@/components/common/cursor";

const focusIcons = [
  FrontendIcon,
  MobileIcon,
  BackendIcon,
  DesignIcon,
  AiIcon,
  TrainingIcon,
];

const Focus = () => {
  const t = useTranslations();
  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const [showCursor, setShowCursor] = useState(false);

  const cardsCount = t.focus.cards.length;

  // Progress bar
  const progressWidth = useTransform(scrollYProgress, [0, 1], ["0", "100%"]);

  return (
    <section
      ref={ref}
      id='focus'
      className='relative mt-16 md:mt-0'
      style={{
        height: `${100 + cardsCount * 100}dvh`,
      }}
    >
      <Cursor show={showCursor} label='SCROLL' />
      {/* Desktop Version */}
      <div className='sticky hidden lg:block top-0 h-dvh overflow-hidden'>
        <div
          onMouseEnter={() => setShowCursor(true)}
          onMouseLeave={() => setShowCursor(false)}
          className='container mx-auto px-0 h-full flex flex-col justify-center'
        >
          <div className='mb-8'>
            <SectionHeader label={t.focus.label} title={t.focus.title} />
          </div>
          <div className='relative'>
            <div
              className='relative h-[350px]'
              style={{ cursor: showCursor ? "none" : "auto" }}
            >
              {t.focus.cards.map((card, index) => {
                const IconComponent = focusIcons[index];
                return (
                  <FocusCardDesktop
                    key={index}
                    icon={IconComponent}
                    title={card.title}
                    text={card.text}
                    index={index}
                    scrollYProgress={scrollYProgress}
                    cardsCount={cardsCount}
                  />
                );
              })}
            </div>

            <div className='mt-8'>
              <motion.div
                className='h-2 bg-brand-violet-500'
                style={{ width: progressWidth }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Version */}
      <div className='sticky block lg:hidden top-0 left-0 w-full h-dvh px-6 pt-28'>
        <div className='mb-6'>
          <SectionHeader label={t.focus.label} title={t.focus.title} />
        </div>
        <div className='relative h-[480px]'>
          {t.focus.cards.map((card, index) => {
            const IconComponent = focusIcons[index];
            return (
              <FocusCardMobile
                key={index}
                icon={IconComponent}
                title={card.title}
                text={card.text}
                index={index}
                scrollYProgress={scrollYProgress}
                cardsCount={cardsCount}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Focus;
